import { handicapBaseDate } from '@/orm/catalogs/Handicaps'
import { conditionallyValidFinishes, implicitResult, neverValidFinishes } from '@/orm/catalogs/OtherResults'
import Event from '@/orm/models/Event'
import { DateFormat, ds } from '@/plugins/i18nFormat'
import { parseResultFormula } from '@/orm/scoring'
import nvl from '@/utils/nvl'
import EventBoatRace from '@model/EventBoatRace'
import EventClass from '@model/EventClass'
import EventRaceDetailedClass, { SPECIAL_BEHAVIOR_CONTINUOUS_START } from '@model/EventRaceDetailedClass'
import EventRaceSkippedClass from '@model/EventRaceSkippedClass'
import EventHeatResult from '@model/EventHeatResult'
import EventResult from '@model/EventResult'
import EventRound from '@model/EventRound'
import { Model, useRepo } from 'pinia-orm'
import { BelongsTo, HasMany, Num, OnDelete, Uid } from 'pinia-orm/decorators'

type Extras = { rooster?: string; [k: string]: any }

function withExtras<T extends object>(model: T, extras: Extras) {
    const reactiveExtras = extras

    return new Proxy(model as any, {
        get(target, prop, receiver) {
            if (prop in reactiveExtras)
                return (reactiveExtras as any)[prop]

            return Reflect.get(target, prop, receiver)
        },
        has(target, prop) {
            return prop in reactiveExtras || prop in target
        },
        ownKeys(target) {
            const keys = new Set([...Reflect.ownKeys(target), ...Object.keys(reactiveExtras)])

            return Array.from(keys)
        },
        getOwnPropertyDescriptor(target, prop) {
            if (prop in reactiveExtras) {
                return { configurable: true, enumerable: true, value: (reactiveExtras as any)[prop], writable: true }
            }

            return Reflect.getOwnPropertyDescriptor(target, prop as any)
        },
    })
}

// Keep boats that share the same points in the same heat. When redistributing a finished round into
// the next round's heats, a points tie can straddle a heat boundary (equal-scoring boats placed into
// different heats only to hit the planned per-group counts). This adjusts the boundaries so no points
// value is split across two heats: by default the whole tied set is pushed down into the lower (worse,
// higher-numbered) heat, even if the higher (better) heat then holds fewer boats than planned. Only if
// pushing a set down would leave the higher heat empty is the whole set pulled up into the higher heat
// instead (which then exceeds its planned size). Boundaries are processed best-first, so a set pushed
// down is re-checked against the next boundary. Membership is all that matters here - intra-heat order
// is irrelevant (the views re-sort heat boats by sail number).
export function enforceEqualPointsTogether(groups: any[][], pointsById: Map<string, number>): any[][] {
    const pointsOf = (boat: any) => pointsById.get(boat.id) ?? Number.MAX_SAFE_INTEGER

    for (let k = 0; k < groups.length - 1; k++) {
        const upper = groups[k] // the better / lower-numbered heat
        const lower = groups[k + 1] // the worse / higher-numbered heat

        if (upper.length === 0)
            continue

        // Points values present in the lower heat, so we can spot the ones straddling the boundary.
        const lowerValues = new Set(lower.map(pointsOf))
        const straddling = new Set(upper.map(pointsOf).filter((p) => lowerValues.has(p)))

        if (straddling.size === 0)
            continue

        const upperKept = upper.filter((boat) => !straddling.has(pointsOf(boat)))

        if (upperKept.length > 0) {
            // Push the whole tied set(s) down: they join their equal-scoring peers in the lower heat.
            const pushed = upper.filter((boat) => straddling.has(pointsOf(boat)))
            groups[k] = upperKept
            groups[k + 1] = [...pushed, ...lower]
        } else {
            // Pushing down would empty the higher heat, so pull the whole set up into it instead.
            const pulled = lower.filter((boat) => straddling.has(pointsOf(boat)))
            groups[k] = [...upper, ...pulled]
            groups[k + 1] = lower.filter((boat) => !straddling.has(pointsOf(boat)))
        }
    }

    return groups
}

export default class EventRace extends Model {
    static entity = 'events-races'

    @Uid()
    declare id: string
    @Uid()
    declare eventId: string
    @BelongsTo(() => Event, 'eventId')
    declare event: Event
    @Num(0, { notNullable: true })
    declare race: number
    @HasMany(() => EventResult, 'eventRaceId') @OnDelete('cascade')
    declare results: EventResult[]
    @HasMany(() => EventRound, 'eventRaceId') @OnDelete('cascade')
    declare rounds: EventRound[]
    @HasMany(() => EventHeatResult, 'eventRaceId') @OnDelete('cascade')
    declare heatResults: EventHeatResult[]
    @HasMany(() => EventRaceSkippedClass, 'eventRaceId') @OnDelete('cascade')
    declare skippedClasses: EventRaceSkippedClass[]
    @HasMany(() => EventRaceDetailedClass, 'eventRaceId') @OnDelete('cascade')
    declare detailedClasses: EventRaceDetailedClass[]
    @HasMany(() => EventBoatRace, 'eventRaceId') @OnDelete('cascade')
    declare eventBoatRaces: EventBoatRace[]
    declare __startTimeCache: Map<
        string,
        { startTime: Date | string | null; isContinuous: boolean; lastDetailedClasses: EventRaceDetailedClass[] }
    >

    get dump() {
        return {
            race: this.race,
            results: this.results.map((result) => result.dump),
            rounds: this.rounds.map((round) => round.dump),
            heatResults: this.heatResults.map((heatResult) => heatResult.dump),
            skippedClasses: this.skippedClasses.map((skippedClass) => skippedClass.dump),
            detailedClasses: this.detailedClasses.map((detailedClass) => detailedClass.dump),
        }
    }

    startTime(boatClass, formatted = false, boatId: string | null = null) {
        if (this.__startTimeCache === undefined) {
            this.__startTimeCache = new Map()
        }

        const cacheEntry = this.__startTimeCache.get(boatClass)
        const isCacheValid = cacheEntry !== undefined && cacheEntry.lastDetailedClasses === this.detailedClasses

        if (isCacheValid && !cacheEntry.isContinuous) {
            return (formatted && cacheEntry.startTime !== null)
                ? ds(cacheEntry.startTime, DateFormat.Time)
                : cacheEntry.startTime
        }

        let isContinuous: boolean
        let detail: EventRaceDetailedClass | null = null

        if (isCacheValid) {
            isContinuous = cacheEntry.isContinuous
        } else {
            detail = useRepo(EventRaceDetailedClass).where('eventRaceId', this.id).where('boatClassId', boatClass)
                .first()
            isContinuous = detail?.specialBehavior === SPECIAL_BEHAVIOR_CONTINUOUS_START
        }

        let startTime: Date | string | null

        if (isContinuous) {
            startTime = null

            if (boatId !== null && this.race > 1) {
                const previousRace = useRepo(EventRace).where('eventId', this.eventId).where('race', this.race - 1)
                    .first()

                if (previousRace !== null) {
                    const previousResult = useRepo(EventResult).where('eventRaceId', previousRace.id).where(
                        'eventBoatId',
                        boatId,
                    ).first()

                    if (previousResult !== null && previousResult.finishTime !== null) {
                        startTime = previousResult.finishTime
                    }
                }
            }

            if (startTime === null) {
                const today = new Date(handicapBaseDate)
                today.setHours(0, 0, 0, 0)
                startTime = today
            }
        } else {
            if (detail === null) {
                detail = useRepo(EventRaceDetailedClass).where('eventRaceId', this.id).where('boatClassId', boatClass)
                    .first()
            }

            if (detail !== null) {
                startTime = detail.startTime
            } else {
                const today = new Date(handicapBaseDate)
                today.setHours(0, 0, 0, 0)
                startTime = today
            }
        }

        if (!isCacheValid) {
            this.__startTimeCache.set(boatClass, { startTime, isContinuous, lastDetailedClasses: this.detailedClasses })
        }

        return (formatted && startTime !== null) ? ds(startTime, DateFormat.Time) : startTime
    }

    filteredResults(boatClass = null) {
        if (this.results === undefined || this.results === null)
            return []

        return (boatClass === null || boatClass === '-all')
            ? this.results
            : this.results.filter(result => result?.boat?.boatClassId === boatClass)
    }

    orderedResults(boatClass = null) {
        const results = this.filteredResults(boatClass)
            .sort(
                (a, b) => (a.boat?.boatClassId === b.boat?.boatClassId
                    ? a.orderTo(b)
                    : a.boat?.eventClassName.localeCompare(b.boat?.eventClassName)),
            )

        const precalculated = results.map((result) => ({
            ...result,
            points: result.points,
        }))

        return results.map(result =>
            withExtras(result, {
                rooster: precalculated.filter(r => r.boat?.boatClassId === result.boat?.boatClassId)
                    .findIndex(ri => ri.points === result.points) + 1,
            })
        )
    }

    filteredRounds(boatClass = null) {
        if (this.rounds === undefined || this.rounds === null)
            return []

        return (boatClass === null || boatClass === '-all')
            ? this.rounds
            : this.rounds.filter(round => round?.boat?.boatClassId === boatClass)
    }

    orderedRounds(boatClass = null) {
        const rounds = this.filteredRounds(boatClass)
            .sort((a, b) => (a.counter - b.counter))

        return rounds.map((round, index) => ({
            ...round,
            rooster: index + 1,
            round: rounds.slice(0, index + 1).filter(r => r.eventBoatId === round.eventBoatId).length,
        }))
    }

    get anyResults(): boolean {
        return this.filteredResults().length > 0
    }

    totalBoats(boatClass = null) {
        // useRepo( Event ).find( this.eventId )
        return this.event?.filteredBoats((boatClass === null || boatClass === '-all') ? null : boatClass).length ?? 0
    }

    // Start lists of all heats of a given (1-based) round, as EventBoat[][] (index = heat - 1).
    // Round 1 of the first elimination race is seeded from the entry list. Round 1 of a following
    // elimination race is seeded from the previous race's final standings via eliminationRaceDistributionMode
    // (round-robin or snake). Every later round is derived from the previous round's finishes: boats are
    // grouped by their place (rank), and whole rank-blocks - never split - are handed out to the new heats,
    // best places going to the lowest-numbered heat (so e.g. all 4th places end up together in one heat).
    // Returns an empty array when the round cannot be determined yet (previous round / race not finished).
    // Resolve the EventClass for a boat class without relying on the (possibly unloaded, e.g. for a
    // just-created race) event relation.
    eventClassOf(boatClass) {
        return this.event?.getClass(boatClass)
            ?? useRepo(EventClass).where('eventId', this.eventId).where('boatClassId', boatClass).with('boatClass')
                .first()
    }

    // The previous race (by race number), resolved without relying on the event relation.
    previousRace() {
        return this.event?.races?.find((r) => r.race === this.race - 1)
            ?? useRepo(EventRace).where('eventId', this.eventId).where('race', this.race - 1).first()
    }

    eliminationRoundHeats(boatClass, round) {
        const eventClass = this.eventClassOf(boatClass)

        if (eventClass === null || eventClass === undefined || !eventClass.isElimination)
            return []

        if (round === 1) {
            if (this.race === 1)
                return eventClass.eliminationFirstRoundHeats()

            // Following elimination race: seed from the previous race's final standings (once they exist).
            return this.previousRace()?.nextRaceFirstRoundHeats(boatClass) ?? []
        }

        const prevHeats = this.eliminationRoundHeats(boatClass, round - 1)

        if (prevHeats.length === 0)
            return []

        // Group the previous round's boats by place (rank); bail out until every boat has finished.
        const blocksByRank: Map<number, { boat: any; heat: number; order: number }[]> = new Map()
        // Each boat's heat points, so equal-scoring boats can be kept together across heats (below).
        const pointsById: Map<string, number> = new Map()

        for (let h = 0; h < prevHeats.length; h++) {
            const ordered = this.orderedHeatResults(boatClass, round - 1, h + 1)
            const finishedIds = ordered.map((r) => r.eventBoatId)

            if (prevHeats[h].some((boat) => !finishedIds.includes(boat.id)))
                return []

            const heatPoints = ordered.map((r) => r.points)

            ordered.forEach((r) => {
                // Rank is the boat's place in its heat by points (competition ranking, ties shared) -
                // not the physical finish order - so a scoring penalty (e.g. SCP) demotes it correctly.
                const rank = heatPoints.filter((p) => p < r.points).length + 1
                const block = blocksByRank.get(rank) ?? []
                block.push({ boat: r.boat, heat: h + 1, order: r.order ?? Number.MAX_SAFE_INTEGER })
                blocksByRank.set(rank, block)
                pointsById.set(r.boat.id, r.points)
            })
        }

        // One block per place, ordered best-first; within a block boats are ordered by heat.
        const blocks = [...blocksByRank.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([, arr]) => arr.sort((a, b) => (a.heat - b.heat) || (a.order - b.order)).map((x) => x.boat))

        const heats = eventClass.eliminationHeats

        // Heat-size key: each listed count is how many top ranks advance from every source heat into
        // that destination group (a rank block holds one boat per source heat), and the last group
        // absorbs every remaining rank. E.g. "3,4,*" -> group 1 gets ranks 1-3, group 2 ranks 4-7,
        // the last group everyone else.
        if (eventClass.eliminationRoundDistributionMode === 'heat-size') {
            const heatGroups: any[][] = []
            let cursor = 0

            for (const count of eventClass.heatSizeGroupBlockCounts(blocks.length)) {
                heatGroups.push(blocks.slice(cursor, cursor + count).flat())
                cursor += count
            }

            return enforceEqualPointsTogether(heatGroups, pointsById)
        }

        // Distribute whole rank-blocks across the heats (block counts differ by at most one). The
        // distribution mode decides whether the lower-numbered heats get more places (moreFirst) or fewer.
        const base = Math.floor(blocks.length / heats)
        const extra = blocks.length % heats
        const moreFirst = eventClass.eliminationRoundDistributionMode === 'moreFirst'

        const groups: any[][] = []
        let idx = 0

        for (let k = 0; k < heats; k++) {
            const count = base + (moreFirst ? (k < extra ? 1 : 0) : (k >= heats - extra ? 1 : 0))
            groups.push(blocks.slice(idx, idx + count).flat())
            idx += count
        }

        return enforceEqualPointsTogether(groups, pointsById)
    }

    // Start list of a single heat (1-based round & heat) of this race.
    eliminationStartList(boatClass, round, heat) {
        return this.eliminationRoundHeats(boatClass, round)[heat - 1] ?? []
    }

    // Number of boats in a given heat of a given round of this race.
    eliminationHeatBoatCount(boatClass, round, heat) {
        return this.eliminationStartList(boatClass, round, heat).length
    }

    // Final elimination standings after the last round: all boats of the gold heat ordered by points,
    // then the silver heat, and so on. The groups are simply stacked without a gap: each following
    // group's points continue right after the previous group's boat count, so the first boat of the
    // second heat is exactly N + 1 where N is the number of boats in the first heat. Every entry is an
    // EventHeatResult carrying `rank`, `heatName` (its last-round group) and `points` (the running total).
    // Returns an empty array until the last round is fully finished.
    eliminationResults(boatClass) {
        const eventClass = this.eventClassOf(boatClass)

        if (eventClass === null || eventClass === undefined || !eventClass.isElimination)
            return []

        const lastRound = eventClass.eliminationRounds
        const heats = this.eliminationRoundHeats(boatClass, lastRound)

        if (heats.length === 0)
            return []

        const standings: any[] = []
        let offset = 0
        let rank = 0

        for (let h = 0; h < heats.length; h++) {
            const heatBoats = heats[h].length
            const results = this.filteredHeatResults(boatClass, lastRound, h + 1)
            const finishedIds = results.map((r) => r.eventBoatId)

            if (heats[h].some((boat) => !finishedIds.includes(boat.id)))
                return []

            const scored = results
                .map((r) => ({ result: r, points: r.calculatePoints(heatBoats) }))
                .sort((a, b) => (a.points - b.points) || a.result.orderTo(b.result))

            scored.forEach(({ result, points }) => {
                standings.push(withExtras(result, {
                    rank: ++rank,
                    heatName: eventClass.eliminationHeatName(lastRound, h + 1),
                    points: offset + points,
                }))
            })

            // The next group starts right after this one: its first boat scores heatBoats + 1.
            offset += heatBoats
        }

        return standings
    }

    // Heats of the first round of the elimination race that FOLLOWS this one, seeded from this race's
    // final standings via eliminationRaceDistributionMode (round-robin / snake). Empty until this race
    // is finished. Also usable before the next race exists (e.g. its yet-unsaved start list preview).
    nextRaceFirstRoundHeats(boatClass) {
        const eventClass = this.eventClassOf(boatClass)

        if (eventClass === null || eventClass === undefined || !eventClass.isElimination)
            return []

        const standings = this.eliminationResults(boatClass)

        if (standings.length === 0)
            return []

        return eventClass.distributeIntoHeats(
            standings.map((entry) => entry.boat),
            eventClass.eliminationRaceDistributionMode,
        )
    }

    // Materialize the elimination standings into real EventResult records for this race + class, so
    // the normal results display / print / whole-event scoring (with discards) work unchanged. Each
    // record stores the total points both as `order` (shown in the result column) and `fixedPoints`
    // (used verbatim for scoring), plus the boat's last-round code for display and validity.
    writeEliminationResults(boatClass) {
        const standings = this.eliminationResults(boatClass)

        const existing = useRepo(EventResult).where('eventRaceId', this.id).with('boat').get()
            .filter((r) => r.boat?.boatClassId === boatClass)

        if (existing.length > 0)
            useRepo(EventResult).destroy(existing.map((r) => r.id))

        // Nothing to write while the elimination is not yet complete - stale results (if any) are cleared above.
        if (standings.length > 0)
            useRepo(EventResult).save(standings.map((entry) => ({
                eventId: this.eventId,
                eventRaceId: this.id,
                eventBoatId: entry.eventBoatId,
                order: entry.points,
                other: entry.other,
                attr: entry.attr,
                fixedPoints: entry.points,
                counter: entry.rank,
                finishTime: null,
                ts: new Date(),
            })))

        if (existing.length > 0 || standings.length > 0)
            this.event?.setModified()
    }

    // Whether the current elimination standings differ from what is stored in EventResult (including
    // the case where nothing is stored yet). Drives the "write race results" prompt.
    eliminationResultsDiverged(boatClass) {
        const standings = this.eliminationResults(boatClass)

        if (standings.length === 0)
            return false

        const stored = useRepo(EventResult).where('eventRaceId', this.id).with('boat').get()
            .filter((r) => r.boat?.boatClassId === boatClass)

        if (stored.length !== standings.length)
            return true

        const storedByBoat = new Map(stored.map((r) => [r.eventBoatId, r]))

        return standings.some((entry) => {
            const r = storedByBoat.get(entry.eventBoatId)

            return !r || r.fixedPoints !== entry.points || r.other !== entry.other
                || (r.attr ?? null) !== (entry.attr ?? null)
        })
    }

    // --- Heat finishes (EventHeatResult) --------------------------------------------------------
    // Scoped by this race + round + heat + the boat's class. Mirrors the EventResult finish flow,
    // but over EventHeatResult (no counter / rounds / finishTime / handicap).

    filteredHeatResults(boatClass, round, heat) {
        return useRepo(EventHeatResult)
            .where('eventRaceId', this.id)
            .where('round', round)
            .where('heat', heat)
            .with('boat', (q) => {
                q.with('boatClass')
                q.with('crew', (qc) => {
                    qc.with('member')
                })
            })
            .get()
            .filter((hr) => hr.boat?.boatClassId === boatClass)
    }

    // Heat finishes ordered for display, each carrying a `rooster` rank and its `points`.
    orderedHeatResults(boatClass, round, heat) {
        const boats = this.eliminationHeatBoatCount(boatClass, round, heat)
        const results = this.filteredHeatResults(boatClass, round, heat).sort((a, b) => a.orderTo(b))
        const points = results.map((r) => r.calculatePoints(boats))

        return results.map((result, i) =>
            withExtras(result, {
                rooster: points.findIndex((p) => p === points[i]) + 1,
                points: points[i],
            })
        )
    }

    heatFinishedBoatIds(boatClass, round, heat) {
        return this.filteredHeatResults(boatClass, round, heat).map((hr) => hr.eventBoatId)
    }

    addHeatFinish(boatClass, round, heat, boatId, otherCode = null, attr = null) {
        const info = parseResultFormula(otherCode)

        if (info === null)
            return null

        const ordered = this.filteredHeatResults(boatClass, round, heat).filter((hr) => hr.order !== null)
        const order = info.needOrder
            ? (ordered.length === 0 ? 1 : Math.max(...ordered.map((hr) => hr.order)) + 1)
            : null

        const saved = useRepo(EventHeatResult).save({
            eventId: this.eventId,
            eventRaceId: this.id,
            eventBoatId: boatId,
            round,
            heat,
            order,
            other: otherCode,
            attr,
            ts: new Date(),
        })

        this.event?.setModified()

        return saved
    }

    // Bulk-assign a result code to every start-list boat of the heat that has no finish yet.
    addMissingHeatResults(boatClass, round, heat, otherCode) {
        const finished = this.heatFinishedBoatIds(boatClass, round, heat)

        const records = this.eliminationStartList(boatClass, round, heat)
            .filter((boat) => !finished.includes(boat.id))
            .map((boat) => ({
                eventId: this.eventId,
                eventRaceId: this.id,
                eventBoatId: boat.id,
                round,
                heat,
                order: null,
                other: otherCode,
                attr: null,
                ts: new Date(),
            }))

        if (records.length > 0) {
            useRepo(EventHeatResult).save(records)
            this.event?.setModified()
        }
    }

    removeHeatResult(heatResult) {
        const removedOrder = heatResult.order
        const boatClass = heatResult.boat?.boatClassId

        useRepo(EventHeatResult).destroy(heatResult.id)

        if (removedOrder !== null && removedOrder !== 0) {
            const affected = this.filteredHeatResults(boatClass, heatResult.round, heatResult.heat)
                .filter((hr) => hr.order !== null && hr.order > removedOrder)

            useRepo(EventHeatResult).save(affected.map((hr) => ({ id: hr.id, order: hr.order - 1 })))
        }

        this.event?.setModified()
    }

    setHeatResultOrder(heatResult, order) {
        const boatClass = heatResult.boat?.boatClassId
        const removedOrder = heatResult.order

        const results = this.filteredHeatResults(boatClass, heatResult.round, heatResult.heat).filter((hr) =>
            hr.order !== null
        )
        const maxOrder = (results.length > 0 ? Math.max(...results.map((hr) => hr.order)) : 0)
            + (removedOrder === null ? 1 : 0)

        if (order < 1 || order > maxOrder || order === removedOrder)
            return

        if (removedOrder === null && order === maxOrder) {
            useRepo(EventHeatResult).save({ id: heatResult.id, order, other: null })
            this.event?.setModified()
            return
        }

        const affected = results.filter((res) =>
            (removedOrder !== null && removedOrder < order)
                ? res.order <= order && res.order > removedOrder
                : res.order >= order && (removedOrder === null || res.order < removedOrder)
        )

        useRepo(EventHeatResult).save(affected.map((hr) => ({
            id: hr.id,
            order: hr.order + ((removedOrder !== null && removedOrder < order) ? -1 : 1),
        })))

        useRepo(EventHeatResult).save({ id: heatResult.id, order, other: null })

        this.event?.setModified()
    }

    setHeatResultOther(heatResult, other, attr = null) {
        const info = parseResultFormula(other)

        if (info === null)
            return

        const boatClass = heatResult.boat?.boatClassId
        const removedOrder = heatResult.order

        if (info.needOrder) {
            if (heatResult.order === null) {
                const results = this.filteredHeatResults(boatClass, heatResult.round, heatResult.heat).filter((hr) =>
                    hr.order !== null
                )
                useRepo(EventHeatResult).save({
                    id: heatResult.id,
                    order: results.length === 0 ? 1 : Math.max(...results.map((hr) => hr.order)) + 1,
                })
            }

            useRepo(EventHeatResult).save({ id: heatResult.id, other, attr })
            this.event?.setModified()
            return
        }

        if (info.orderBehavior === 'preserve') {
            useRepo(EventHeatResult).save({ id: heatResult.id, other, attr })
            this.event?.setModified()
            return
        }

        if (removedOrder !== null) {
            const affected = this.filteredHeatResults(boatClass, heatResult.round, heatResult.heat)
                .filter((hr) => hr.order !== null && hr.order > removedOrder)

            useRepo(EventHeatResult).save(affected.map((hr) => ({ id: hr.id, order: hr.order - 1 })))
        }

        useRepo(EventHeatResult).save({ id: heatResult.id, order: null, other, attr })
        this.event?.setModified()
    }

    allBoatsOrdered(boatClass = null) {
        const finishedBoats = this.filteredResults(boatClass).map(r => r.eventBoatId)

        return this.event?.filteredBoats((boatClass === null || boatClass === '-all') ? null : boatClass)
            ?.map(boat => {
                const b: any = { ...boat, sailNo: boat.sailNo, finished: finishedBoats.includes(boat.id) }
                b.boatClass = boat.boatClass
                b.crew = boat.crew
                return b
            })
            .sort((a, b) => (a.sailNumber - b.sailNumber)) ?? []
    }

    finishedBoats(boatClass = null) {
        return this.filteredResults(boatClass).length
    }

    totalBoatsWithoutSkipped(boatClass = null) {
        const skippedClasses = this.skippedClasses !== undefined ? this.skippedClasses.map(c => c.boatClassId) : []

        // useRepo( Event ).find( this.eventId )
        return this.event
            ?.filteredBoats((boatClass === null || boatClass === '-all') ? null : boatClass)
            ?.filter(b => !skippedClasses.includes(b.boatClassId)).length ?? 0
    }

    unfinishedBoats(boatClass = null) {
        return this.totalBoatsWithoutSkipped(boatClass) - this.finishedBoats(boatClass)
    }

    boatResult(boatId) {
        const results = this.results?.filter(result => result.eventBoatId === boatId)

        return results.length > 0 ? nvl(results[0].result, implicitResult) : implicitResult
    }

    boatFullResult(boatId) {
        const results = this.results?.filter(result => result.eventBoatId === boatId)

        return results[0]
    }

    boatDecidedResult(boatId) {
        const results = this.results?.filter(result => result.eventBoatId === boatId)

        return results.length > 0 && results[0].result !== null && results[0].result !== undefined
    }

    boatIsValidFinish(boatId) {
        const fullResult = this.boatFullResult(boatId)

        if (!fullResult)
            return false

        const result = fullResult.result

        if (neverValidFinishes.includes(result))
            return false

        if (conditionallyValidFinishes.includes(result))
            return fullResult.orderInclHandicap !== null

        return true
    }

    boatPoints(boatId, defaultPoints = 0, totalBoats: number | null = null) {
        const results = this.results?.filter(result => result.eventBoatId === boatId)

        return results.length > 0 ? nvl(results[0].calculatePoints(totalBoats), defaultPoints) : defaultPoints
    }

    isSkippedBoatClass(boatClassId) {
        if (this.race >= 2 && this.event?.getClass(boatClassId)?.isLongDistance) {
            return true
        }

        return this.skippedClasses.find(skippedClass => skippedClass.boatClassId === boatClassId) !== undefined
    }

    static piniaOptions = {
        persist: false,
    }
}
